// OPTIMUS, aliado comercial de Solarix (Diego, 12-oct noche, ADENDA punto c, MANDA). Una venta de un vendedor de Optimus
// paga 15 % de comisión (la del vendedor, 10 % por omisión, + 5 % de Juan Carlos Cortés) y su PRECIO sube para que Solarix
// gane lo mismo que en una venta normal con 6 %:
//     precio Optimus = precio de tabla × (1 − 6 %) ÷ (1 − 15 %)        (≈ +10.588 %)
// en contado, lista, piso, extras de estructura, renglones fijos de la tabla y equipos de respaldo; el USD/W que se enseña
// es el del precio Optimus. Así lo que le queda a Solarix después de comisiones es IGUAL al de la venta normal, peso por peso:
//     P' · (1 − 15 %) − costo = P · (1 − 6 %) − costo.
// El factor se CONGELA en la cotización (ParametrosCalculo.precio_optimus) al crearla: si Diego marca o desmarca a un vendedor
// después, sus cotizaciones ya hechas NO cambian de precio solas (aviso PRECIO_OPTIMUS y recálculo explícito).
import { z } from 'zod';
import { Fecha } from './basicos.ts';
import { OPTIMUS } from '../constantes.ts';
import { ErrorCotizador } from '../errores.ts';
import { comisionAliadoDe, comisionDe } from './usuario.ts';
import type { Usuario } from './usuario.ts';

/** (1 − base) ÷ (1 − total), a 6 decimales. Con 6 % y 15 %: 1.105882. */
export function factorOptimus(comisionBasePct: number, comisionTotalPct: number): number {
  if (!(comisionTotalPct >= 0 && comisionTotalPct < 90)) throw new Error(`factorOptimus: comisión total imposible (${comisionTotalPct} %)`);
  return Math.round(((1 - comisionBasePct / 100) / (1 - comisionTotalPct / 100)) * 1e6) / 1e6;
}

/** El precio de Optimus CONGELADO en una cotización. Candado ruidoso: el factor tiene que ser el que sale de sus comisiones
 *  (un factor escrito a mano o de otra comisión no valida). */
export const PrecioOptimus = z
  .strictObject({
    /** Comisión del vendedor de Optimus con la que se fijó el precio (la de su ficha o 10 %). */
    comision_vendedor_pct: z.number().min(0).max(50),
    /** La de Juan Carlos Cortés por Optimus (5 %). */
    comision_aliado_pct: z.number().min(0).max(50),
    /** La de una venta normal con la que Solarix debe ganar lo mismo (6 %). */
    comision_base_pct: z.number().min(0).max(50),
    /** Lo que multiplica al precio de la tabla: (1 − base) ÷ (1 − vendedor − aliado). */
    factor: z.number().min(1).max(2),
    /** Día (Hermosillo) en que se fijó. */
    fijado_el: Fecha,
  })
  .refine((p) => Math.abs(p.factor - factorOptimus(p.comision_base_pct, p.comision_vendedor_pct + p.comision_aliado_pct)) < 1e-9, {
    message: 'El factor del precio de Optimus no es el que sale de sus comisiones: (1 − base) ÷ (1 − vendedor − aliado).',
    path: ['factor'],
  });
export type PrecioOptimus = z.infer<typeof PrecioOptimus>;

type VendedorOptimus = Pick<Usuario, 'rol'> & { comision_pct?: number | null | undefined; optimus?: boolean | undefined };

/** Comisión total de una venta de Optimus permitida (la del vendedor + la de Juan Carlos): al menos la de una venta normal
 *  (6 %; con menos, el factor quedaría abajo de 1 y el precio de Optimus abajo de la tabla) y como mucho 40 % (arriba, el
 *  precio se dispara: × 1.57 o más). */
export const COMISION_TOTAL_OPTIMUS = Object.freeze({ min_pct: OPTIMUS.comision_base_pct, max_pct: 40 });

/** Lo que impide que un vendedor de Optimus cotice, para DIEGO (con cifras; se dice al guardar su ficha). null = nada.
 *  Hallazgo del 13-oct: con 0 % o 0.5 % la ficha se guardaba y después el vendedor no podía crear ninguna cotización
 *  (500 INTERNO: "factor ≥ 1"). Ahora truena al guardar, con el porqué. */
export function problemaComisionOptimus(u: VendedorOptimus): string | null {
  if (!u.optimus) return null;
  const vendedor = comisionDe(u);
  const total = vendedor + comisionAliadoDe(u);
  const suma = `Con Optimus la comisión total es la del vendedor + ${OPTIMUS.comision_aliado_pct} % de ${OPTIMUS.aliado_cobra}; con ${vendedor} % suma ${total} %`;
  if (total < COMISION_TOTAL_OPTIMUS.min_pct)
    return `${suma}, menos que el ${OPTIMUS.comision_base_pct} % de una venta normal: su precio quedaría abajo de la tabla. Mínimo ${COMISION_TOTAL_OPTIMUS.min_pct - OPTIMUS.comision_aliado_pct} % del vendedor (o quítale la casilla de Optimus).`;
  if (total > COMISION_TOTAL_OPTIMUS.max_pct) return `${suma} (máximo ${COMISION_TOTAL_OPTIMUS.max_pct} %).`;
  return null;
}

/** El factor del precio de Optimus de este vendedor HOY (1 = venta normal). Truena con CONFLICTO (no con 500) si su ficha
 *  tiene una comisión que no sirve (una guardada antes de que existiera el candado): el mensaje lo lee el VENDEDOR, sin cifras. */
export function factorOptimusDe(v: VendedorOptimus): number {
  if (!v.optimus) return 1;
  if (problemaComisionOptimus(v))
    throw new ErrorCotizador('CONFLICTO', 'Tu ficha de vendedor de Optimus tiene una comisión que no sirve para cotizar. Solo Diego la corrige (Configuración → Usuarios): pídeselo y vuelve a intentarlo.');
  return factorOptimus(OPTIMUS.comision_base_pct, comisionDe(v) + comisionAliadoDe(v));
}

/** El precio de Optimus que le toca HOY a una cotización de este vendedor (undefined = venta normal). */
export function precioOptimusPara(v: VendedorOptimus, hoy: Fecha): PrecioOptimus | undefined {
  if (!v.optimus) return undefined;
  const factor = factorOptimusDe(v);
  return { comision_vendedor_pct: comisionDe(v), comision_aliado_pct: comisionAliadoDe(v), comision_base_pct: OPTIMUS.comision_base_pct, factor, fijado_el: hoy };
}

/** ¿La cotización tiene el precio que le toca a su vendedor HOY? Con precio de Optimus si y solo si el vendedor es de
 *  Optimus y, si lo es, con SUS comisiones de hoy (hallazgo del 13-oct: si Diego le cambiaba la comisión, las cotizaciones
 *  ya hechas seguían con el factor viejo sin aviso). Una ficha cuya comisión no sirve cuenta como "no al día". */
export function precioOptimusAlDia(p: { precio_optimus?: PrecioOptimus | undefined }, v: VendedorOptimus): boolean {
  if (!!p.precio_optimus !== !!v.optimus) return false;
  if (!p.precio_optimus) return true;
  return p.precio_optimus.comision_vendedor_pct === comisionDe(v) && p.precio_optimus.comision_aliado_pct === comisionAliadoDe(v);
}

/** El margen con el que se juzga el semáforo de una venta con precio de Optimus: el de una venta NORMAL (con la comisión base)
 *  que le dejara a Solarix la misma utilidad con el mismo costo. Margen real × (1 − base) ÷ (1 − comisión total). Con el
 *  precio de tabla de Optimus da el mismo margen que la venta normal a precio de tabla (32 % → 32 %), así el "verde manda"
 *  del asesor y los umbrales de Diego (20 / 30 %) valen igual para los dos. */
export function margenEquivalente(margenRealPct: number, comisionBasePct: number, comisionTotalPct: number): number {
  return (margenRealPct * (1 - comisionBasePct / 100)) / (1 - comisionTotalPct / 100);
}
